import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpResponse, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AssetInput, Asset, IdResponse, QuerySpec } from '../model';
import { MANAGEMENT_API_CONTEXT } from '../variables';
import { ManagementRouteService } from '../common/managementRoute.service';

@Injectable({
  providedIn: 'root',
})
export class AssetService {
  constructor(
    private readonly http: HttpClient,
    private readonly managementRoute: ManagementRouteService
  ) {}

  // ── Overloads kept for backward-compat with callers that pass observe param ──

  public createAsset(assetInput: AssetInput, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<IdResponse>;
  public createAsset(assetInput: AssetInput, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<IdResponse>>;
  public createAsset(assetInput: AssetInput, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<IdResponse>>;
  public createAsset(assetInput: AssetInput): Observable<any> {
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post<IdResponse>(`${url}/assets`, this.toJsonLdAssetInput(assetInput));
  }

  public updateAsset(assetInput: AssetInput, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<any>;
  public updateAsset(assetInput: AssetInput, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<any>>;
  public updateAsset(assetInput: AssetInput, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<any>>;
  public updateAsset(assetInput: AssetInput): Observable<any> {
    if (!(assetInput as any)['@id']) throw new Error('Required parameter @id was null or undefined when calling updateAsset.');

    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.put(`${url}/assets`, this.toJsonLdAssetInput(assetInput));
  }

  public getAsset(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<Asset>;
  public getAsset(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<Asset>>;
  public getAsset(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<Asset>>;
  public getAsset(id: string): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling getAsset.');
    
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.get<any>(`${url}/assets/${encodeURIComponent(id)}`).pipe(
      map(item => this.toAssetInstance(item))
    );
  }

  public removeAsset(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<any>;
  public removeAsset(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<any>>;
  public removeAsset(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<any>>;
  public removeAsset(id: string): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling removeAsset.');
    
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.delete(`${url}/assets/${encodeURIComponent(id)}`);
  }

  public requestAssets(querySpec?: QuerySpec, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<Array<Asset>>;
  public requestAssets(querySpec?: QuerySpec, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<Array<Asset>>>;
  public requestAssets(querySpec?: QuerySpec, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<Array<Asset>>>;
  public requestAssets(querySpec?: QuerySpec): Observable<any> {
    
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post<any[]>(`${url}/assets/request`, this.toJsonLdQuerySpec(querySpec)).pipe(
      map(items => items.map(item => this.toAssetInstance(item)))
    );
  }

  private toJsonLdAssetInput(assetInput: AssetInput): Record<string, any> {
    return {
      '@context': [MANAGEMENT_API_CONTEXT],
      '@type': 'Asset',
      ...assetInput,
    };
  }

  private toAssetInstance(raw: any): any {
    const withAccessor = (obj: Record<string, any>) => ({
      ...obj,
      optionalValue: (_prefix: string, name: string) => obj?.[name] ?? null,
    });
    return {
      ...raw,
      id: raw['@id'] ?? raw['id'],
      properties: withAccessor(raw['properties'] ?? {}),
      dataAddress: withAccessor(raw['dataAddress'] ?? {}),
    };
  }

  private toJsonLdQuerySpec(querySpec?: QuerySpec): Record<string, any> {
    const body: Record<string, any> = {
      '@context': [MANAGEMENT_API_CONTEXT],
      '@type': 'QuerySpec',
      'offset': querySpec?.offset ?? 0,
      'limit': querySpec?.limit ?? 50,
    };
    if (querySpec?.sortOrder) {
      body['sortOrder'] = querySpec.sortOrder;
    }
    if (querySpec?.sortField) {
      body['sortField'] = querySpec.sortField;
    }
    if (querySpec?.filterExpression?.length) {
      body['filterExpression'] = querySpec.filterExpression;
    }
    return body;
  }
}

